import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../../utils/errors';
import {
  useAcceptCustomerSubuserInvite,
  useCustomerSubuserInviteInfo,
} from '../../features/customer-subusers/api';
import './CustomerSubuserInvitePage.scss';

export default function CustomerSubuserInvitePage() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const auth = getAuth();

  const { data, isLoading, isError, refetch } = useCustomerSubuserInviteInfo(token || '');
  const accept = useAcceptCustomerSubuserInvite(token);

  const [authReady, setAuthReady] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isAuthed, setIsAuthed] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setIsAuthed(Boolean(user));
      setUserEmail(user?.email ?? '');
      setAuthReady(true);
    });
  }, [auth]);

  const invitedEmail = (data?.email ?? '').trim().toLowerCase();
  const emailMatches = Boolean(invitedEmail && userEmail.trim().toLowerCase() === invitedEmail);
  const canAccept = authReady && isAuthed && emailMatches && !switchingAccount;
  const wrongAccount = isAuthed && !emailMatches;

  // Keep the invitation token (and any query parameters) through sign-up/sign-in.
  const returnTo = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
  const signUpUrl = `/signup?redirect=${returnTo}`;
  const signInUrl = `/login?redirect=${returnTo}`;

  const handleSwitchAccount = async () => {
    try {
      setSwitchingAccount(true);
      await signOut(auth);
      // The auth listener above will reveal the sign-in and create-account choices.
    } catch (error) {
      console.error(error);
      toast.error(apiErrorMessage(error, 'Could not switch accounts. Please try again.'));
    } finally {
      setSwitchingAccount(false);
    }
  };

  const handleAccept = async () => {
    if (!canAccept || accept.isPending) return;

    try {
      await accept.mutateAsync();
      toast.success('Invitation accepted!');
      nav('/orders');
    } catch (error) {
      console.error(error);
      toast.error(apiErrorMessage(error, 'Failed to accept invitation.'));
    }
  };

  if (isLoading || !authReady) {
    return (
      <main className="subinvite-page">
        <section className="subinvite-shell" aria-busy="true" aria-label="Loading invitation">
          <div className="subinvite-skeleton subinvite-skeleton--title" />
          <div className="subinvite-skeleton subinvite-skeleton--line" />
          <div className="subinvite-skeleton subinvite-skeleton--block" />
        </section>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="subinvite-page">
        <section className="subinvite-shell subinvite-shell--empty">
          <h1>Invitation unavailable</h1>
          <p>
            This invitation link is invalid or has expired. Ask the customer to send you a new one.
          </p>
        </section>
      </main>
    );
  }

  const customerName = data.customerName || data.orgName || 'this customer account';
  const expiration = data.inviteExpiresAt ? new Date(data.inviteExpiresAt).toLocaleString() : null;

  return (
    <main className="subinvite-page">
      <section className="subinvite-shell">
        <header className="subinvite-header">
          <div className="subinvite-eyebrow">ACCOUNT ACCESS INVITATION</div>
          <h1>You're invited to join {customerName}</h1>
          <p className="subinvite-subtitle">
            Set up your access to this customer account in two simple steps.
          </p>
          <div className="subinvite-recipient">
            <span className="subinvite-recipient__label">Invitation sent to</span>
            <strong>{data.email}</strong>
          </div>
        </header>

        <section className="subinvite-section" aria-label="Account access">
          <div className="subinvite-section-heading">
            <span className="subinvite-step">1</span>
            <div>
              <h2>Access your account</h2>
              <p>Use the email address shown on your invitation.</p>
            </div>
          </div>

          {canAccept ? (
            <div className="subinvite-auth-message subinvite-auth-message--success" role="status">
              <span className="subinvite-state-icon" aria-hidden="true">
                ✓
              </span>
              <div>
                <strong>You're signed in</strong>
                <p>
                  Signed in as <strong>{userEmail}</strong>. You're ready to review and accept the
                  invitation.
                </p>
              </div>
            </div>
          ) : wrongAccount ? (
            <div className="subinvite-auth-message subinvite-auth-message--warning" role="alert">
              <span className="subinvite-state-icon" aria-hidden="true">
                !
              </span>
              <div>
                <strong>You're signed in with a different email</strong>
                <p>
                  You're using <strong>{userEmail || 'another account'}</strong>, but this
                  invitation was sent to <strong>{data.email}</strong>. Switch accounts to continue.
                </p>
                <button
                  type="button"
                  className="subinvite-button subinvite-button--primary subinvite-button--inline"
                  onClick={handleSwitchAccount}
                  disabled={switchingAccount || accept.isPending}
                >
                  {switchingAccount ? 'Switching…' : 'Switch account'}
                </button>
              </div>
            </div>
          ) : (
            <div className="subinvite-auth-choice">
              <p>
                <strong>New here?</strong> Create an account. <strong>Already have one?</strong>{' '}
                Sign in. Please use <strong>{data.email}</strong> so we can match your invitation.
              </p>
              <div className="subinvite-button-row">
                <Link className="subinvite-button subinvite-button--primary" to={signUpUrl}>
                  Create account
                </Link>
                <Link className="subinvite-button subinvite-button--secondary" to={signInUrl}>
                  Sign in
                </Link>
              </div>
            </div>
          )}
        </section>

        <section
          className="subinvite-section subinvite-section--details"
          aria-label="Invitation details"
        >
          <div className="subinvite-section-heading subinvite-section-heading--with-action">
            <span className="subinvite-step">2</span>
            <div>
              <h2>Review your invitation</h2>
              <p>Confirm that the details below are correct before accepting.</p>
            </div>
            <button
              className="subinvite-refresh"
              type="button"
              onClick={() => void refetch()}
              disabled={accept.isPending || switchingAccount}
              aria-label="Refresh invitation details"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 7v5h-5M4 17v-5h5" />
                <path d="M5.6 9a7 7 0 0 1 12-2L20 12M4 12l2.4 5a7 7 0 0 0 12-2" />
              </svg>
              Refresh
            </button>
          </div>

          <dl className="subinvite-details">
            <div className="subinvite-details__item">
              <dt>Your name</dt>
              <dd>{data.name || '—'}</dd>
            </div>
            <div className="subinvite-details__item">
              <dt>Customer account</dt>
              <dd>{data.customerName || '—'}</dd>
            </div>
            <div className="subinvite-details__item">
              <dt>Organization</dt>
              <dd>{data.orgName || '—'}</dd>
            </div>
            {expiration && (
              <div className="subinvite-details__item">
                <dt>Invitation expires</dt>
                <dd>{expiration}</dd>
              </div>
            )}
          </dl>
          <p className="subinvite-help-text">
            Once you accept, your user account will be linked to this customer and you can access
            your orders.
          </p>
        </section>

        <footer className="subinvite-footer">
          <div className="subinvite-footer__copy">
            <strong>Ready to get started?</strong>
            <p>
              {canAccept
                ? "You're signed in with the invited email address. You can accept it now."
                : 'Sign in with the invited email address to enable acceptance.'}
            </p>
          </div>
          <button
            className="subinvite-button subinvite-button--primary subinvite-button--submit"
            type="button"
            disabled={!canAccept || accept.isPending}
            onClick={handleAccept}
          >
            {accept.isPending ? 'Accepting…' : 'Accept invitation'}
          </button>
        </footer>
      </section>
    </main>
  );
}
